/** Route table — `react-router-dom@6` data router.
 *
 *  Every route carries a `handle` (`RouteTitleHandle`) — it is the single
 *  source of truth for the `<page>` segment of `document.title`, read by
 *  `<DocumentTitle>` through `useMatches()`. Declaring it here rather than in
 *  each page means a route still has a title while its data loads, when
 *  `RequireAuth` denies the permission, or when the page renders no
 *  `<PageHeader>` at all — and `routes.title.test.ts` can prove every route
 *  has one. `<Navigate>` routes opt out with `{ redirect: true }`.
 */
import { CircularProgress, Box } from '@mui/material';
import React, { Suspense } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';

import { RequireAuth } from '@/auth/RequireAuth';
import { LoginPage } from '@/auth/LoginPage';
import { MFAChallengePage } from '@/auth/MFAChallengePage';
import { MFAEnrollPage } from '@/auth/MFAEnrollPage';
import { InviteAcceptPage } from '@/auth/InviteAcceptPage';
import { PasswordResetConfirmPage } from '@/auth/PasswordResetConfirmPage';
import { PasswordResetRequestPage } from '@/auth/PasswordResetRequestPage';
import { BackupCodesPage } from '@/auth/BackupCodesPage';
import { OAuthHandoffPage } from '@/auth/OAuthHandoffPage';
import { OAUTH_HANDOFF_PATH } from '@/auth/returnTo';
import { AppShell } from '@/layout/AppShell';
import { AIAgentPage } from '@/features/ai-agent/AIAgentPage';
import { AISpendPage } from '@/features/ai-spend/AISpendPage';
import { AIChangeDetailPage } from '@/features/ai-changes/AIChangeDetailPage';
import { AIChangesPage } from '@/features/ai-changes/AIChangesPage';
import { AIPendingChangePage } from '@/features/ai-changes/AIPendingChangePage';
import { AIPendingChangesPage } from '@/features/ai-changes/AIPendingChangesPage';
import { AIProposalReviewPage } from '@/features/ai-changes/AIProposalReviewPage';
import { AuditLogPage } from '@/features/audit/AuditLogPage';
import { ChangesListPage } from '@/features/changes/ChangesListPage';
import { BlacklistsPage } from '@/features/blacklists/BlacklistsPage';
import { PresetsManagePage } from '@/features/presets/PresetsManagePage';
import { BgTasksPage } from '@/features/bgtasks/BgTasksPage';
import { BookingDetailPage } from '@/features/bookings/BookingDetailPage';
import { BookingsPage } from '@/features/bookings/BookingsPage';
import { DeliveryLogDetailPage } from '@/features/delivery-logs/DeliveryLogDetailPage';
import { DeliveryLogsPage } from '@/features/delivery-logs/DeliveryLogsPage';
import { ApiRequestsPage } from '@/features/api-requests/ApiRequestsPage';
import { SubscriptionsPage } from '@/features/subscriptions/SubscriptionsPage';
import { ApiCredentialsPage } from '@/features/api-credentials/ApiCredentialsPage';
import { PartnerCreatePage } from '@/features/partners/PartnerCreatePage';
import { PartnerDetailPage } from '@/features/partners/PartnerDetailPage';
import { PartnersPage } from '@/features/partners/PartnersPage';
import { ExternalAccessPage } from '@/features/external-access/ExternalAccessPage';
import { OnboardingInvitesPage } from '@/features/onboarding-invites/OnboardingInvitesPage';
import { ExtIdsPage } from '@/features/ext-ids/ExtIdsPage';
import { ImportRunsPage } from '@/features/import-runs/ImportRunsPage';
import { OrderDetailPage } from '@/features/orders/OrderDetailPage';
import { OrdersPage } from '@/features/orders/OrdersPage';
import { AiImportPage } from '@/features/ai-import/AiImportPage';
import { DraftsReviewPage } from '@/features/ai-import/DraftsReviewPage';
import { AuxSyncPage } from '@/features/aux-sync/AuxSyncPage';
import { AuxSyncReviewPage } from '@/features/aux-sync/AuxSyncReviewPage';
import { BookingSyncPage } from '@/features/sync/BookingSyncPage';
import { OrderSyncPage } from '@/features/sync/OrderSyncPage';
import { ProductSyncPage } from '@/features/sync/ProductSyncPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { AccessGroupsListPage } from '@/features/rbac/AccessGroupsListPage';
import { TemplateGroupsListPage } from '@/features/rbac/TemplateGroupsListPage';
import { TemplatesListPage } from '@/features/rbac/TemplatesListPage';
import { BatchConfigPage } from '@/features/tour-operators/config-batch/BatchConfigPage';
import { TourOperatorEditPage } from '@/features/tour-operators/TourOperatorEditPage';
import { TourOperatorsListPage } from '@/features/tour-operators/TourOperatorsListPage';
import { TourOperatorSetupPage } from '@/features/tour-operators/setup/TourOperatorSetupPage';
import { ToursListPage } from '@/features/tours/ToursListPage';
import { TourDetailPage } from '@/features/tours/TourDetailPage';
import { RegionsListPage } from '@/features/regions/RegionsListPage';
import { RegionDetailPage } from '@/features/regions/RegionDetailPage';
import { LaunchPoliciesListPage } from '@/features/launch-policies/LaunchPoliciesListPage';
import { NotificationTimelinePage } from '@/features/notification-timeline/OverviewPage';
import { NotificationAnatomyPage } from '@/features/notification-anatomy/AnatomyPage';
import { NotificationsListPage } from '@/features/notifications/NotificationsListPage';
import { NotificationEditPage } from '@/features/notifications/NotificationEditPage';
import { FindReplacePage } from '@/features/find-replace/FindReplacePage';
import { NotificationBlocksListPage } from '@/features/notifications/NotificationBlocksListPage';
import { NotificationFeatureFlagsPage } from '@/features/notification-features/NotificationFeatureFlagsPage';
import { ResellerPoliciesPage } from '@/features/notification-features/ResellerPoliciesPage';
import { BlockListsListPage } from '@/features/notifications/BlockListsListPage';
import { CannedNotificationsListPage } from '@/features/notifications/CannedNotificationsListPage';
import { ThemesListPage } from '@/features/notifications/ThemesListPage';
import { LanguagePoliciesListPage } from '@/features/notifications/LanguagePoliciesListPage';
import { LanguageAliasesListPage } from '@/features/notifications/LanguageAliasesListPage';
import { RulesetsListPage } from '@/features/notifications/RulesetsListPage';
import { RulesetEditPage } from '@/features/notifications/RulesetEditPage';
import { TestRenderPage } from '@/features/notifications/TestRenderPage';
import { MaterialsPage } from '@/features/design-imports/MaterialsPage';
import { ProposalReviewPage } from '@/features/design-imports/ProposalReviewPage';
import { OTAsListPage } from '@/features/otas/OTAsListPage';
import { OTADetailPage } from '@/features/otas/OTADetailPage';
import { TripTypesListPage } from '@/features/trip-types/TripTypesListPage';
import { TripTypeDetailPage } from '@/features/trip-types/TripTypeDetailPage';
import { PreTripItemsPage } from '@/features/pre-trip-items/PreTripItemsPage';
import { PreTripItemDetailPage } from '@/features/pre-trip-items/PreTripItemDetailPage';
import { PreTripItemCategoryDetailPage } from '@/features/pre-trip-items/PreTripItemCategoryDetailPage';
import { ActivitiesListPage } from '@/features/activities/ActivitiesListPage';
import { ActivityRecordPage } from '@/features/activities/record/ActivityRecordPage';
import { ActivityCategoriesListPage } from '@/features/activities/ActivityCategoriesListPage';
import { CategoryRecordPage } from '@/features/activities/category/record/CategoryRecordPage';
import { VehiclesPage } from '@/features/fleet/vehicles/VehiclesPage';
import { BusStopsListPage } from '@/features/bus-stops/BusStopsListPage';
import { BusStopDetailPage } from '@/features/bus-stops/BusStopDetailPage';
import { LineSetsListPage } from '@/features/line-sets/LineSetsListPage';
import { LineSetDetailPage } from '@/features/line-sets/LineSetDetailPage';
import { ItinerariesListPage } from '@/features/itineraries/ItinerariesListPage';
import { ItineraryRecordPage } from '@/features/itineraries/record/ItineraryRecordPage';
import { PreflightTemplatesListPage } from '@/features/preflight/PreflightTemplatesListPage';
import { PreflightGroupsListPage } from '@/features/preflight/PreflightGroupsListPage';
import { PreflightItemsListPage } from '@/features/preflight/PreflightItemsListPage';
import { PreflightUserGroupsPage } from '@/features/preflight/PreflightUserGroupsPage';
import { TagCategorySetsListPage } from '@/features/preflight/TagCategorySetsListPage';
import { TagCanonicalsListPage } from '@/features/tags/TagCanonicalsListPage';
import { TagNameAliasesListPage } from '@/features/tags/TagNameAliasesListPage';
import { TranslationGlossaryPage } from '@/features/translation/TranslationGlossaryPage';
import { TagSchemasListPage } from '@/features/tags/TagSchemasListPage';
import { TagSchemaDetailPage } from '@/features/tags/TagSchemaDetailPage';
import { TagCategoriesListPage } from '@/features/tags/TagCategoriesListPage';
import { TagCategoryDetailPage } from '@/features/tags/TagCategoryDetailPage';
import { TagsListPage } from '@/features/tags/TagsListPage';
import { TagDetailPage } from '@/features/tags/TagDetailPage';
import { McpConnectionsPage } from '@/features/mcp-connections/McpConnectionsPage';
import { KnowledgeGapsPage } from '@/features/knowledge-gaps/KnowledgeGapsPage';
import { McpDiagnoseRunPage } from '@/features/mcp-diagnose-runs/McpDiagnoseRunPage';
import { McpDiagnoseRunsPage } from '@/features/mcp-diagnose-runs/McpDiagnoseRunsPage';
import { McpLimitHitsPage } from '@/features/mcp-limits/McpLimitHitsPage';
import { PlatformNoticesPage } from '@/features/platform-notices/PlatformNoticesPage';
import { SupportTicketDetailPage } from '@/features/support-tickets/SupportTicketDetailPage';
import { SupportTicketsPage } from '@/features/support-tickets/SupportTicketsPage';
import { SessionsPage } from '@/features/sessions/SessionsPage';
import { UserDetailPage } from '@/features/users/UserDetailPage';
import { KIND_COPY, UsersListPage } from '@/features/users/UsersListPage';
import { RootLayout } from '@/RootLayout';

// The help feature bundles all onboarding-story markdown (~160 KB) — keep it
// out of the main chunk.
const HelpPage = React.lazy(() =>
  import('@/features/help/HelpPage').then((m) => ({ default: m.HelpPage })),
);

// Easy onboarding (~70 kB) is a create-time flow: keep it out of the main chunk too. Its only
// other static importers (the tenant-page banner, the advanced wizard's prefill reader) pull
// small modules, not the page.
const EasyOnboardingPage = React.lazy(() =>
  import('@/features/tour-operators/onboard/EasyOnboardingPage').then((m) => ({
    default: m.EasyOnboardingPage,
  })),
);

// Tenant requests approve through easy onboarding's Go live dialog: lazy, like that page, so the
// dialog's module graph stays out of the main chunk.
const TenantRequestsPage = React.lazy(() =>
  import('@/features/tenant-requests/TenantRequestsPage').then((m) => ({
    default: m.TenantRequestsPage,
  })),
);

/** The loading fallback of a lazy route. */
function RouteFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
      <CircularProgress />
    </Box>
  );
}

export const router = createBrowserRouter([
  {
    // RootLayout supplies ErrorBoundary + SnackbarProvider + AuthProvider —
    // nested inside the router so those providers can use `useNavigate()`.
    element: <RootLayout />,
    children: [
      // Public auth routes
      { path: '/auth/login', element: <LoginPage />, handle: { title: 'Sign in' } },
      { path: '/auth/mfa', element: <MFAChallengePage />, handle: { title: 'Two-factor' } },
      {
        path: '/auth/mfa/enroll',
        element: <MFAEnrollPage />,
        handle: { title: 'Set up two-factor' },
      },
      {
        path: '/auth/invite',
        element: <InviteAcceptPage />,
        handle: { title: 'Accept invitation' },
      },
      {
        path: '/auth/password-reset',
        element: <PasswordResetRequestPage />,
        handle: { title: 'Reset password' },
      },
      {
        path: '/auth/password-reset/confirm',
        element: <PasswordResetConfirmPage />,
        handle: { title: 'Choose a new password' },
      },
      {
        path: '/auth/backup-codes',
        element: <BackupCodesPage />,
        handle: { title: 'Backup codes' },
      },
      {
        // The MCP sign-in handoff (backend metaplan A7c): needs a session — RequireAuth signs
        // the user in and returns here with `req` — but no app shell.
        path: OAUTH_HANDOFF_PATH,
        element: (
          <RequireAuth>
            <OAuthHandoffPage />
          </RequireAuth>
        ),
        handle: { title: 'Connect an MCP client' },
      },

      // Authenticated app shell
      {
        path: '/',
        element: (
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <DashboardPage />, handle: { title: 'Dashboard' } },
          {
            // The signed-in user's own MCP connections (backend metaplan A7c), from the user
            // menu. No permission: the backend scopes both calls to the session's user.
            path: 'mcp-connections',
            handle: { title: 'MCP connections' },
            element: <McpConnectionsPage />,
          },
          {
            path: 'users',
            element: <Navigate to="/users/system" replace />,
            handle: { redirect: true },
          },
          ...(['system', 'staff', 'guest'] as const).map((kind) => ({
            path: `users/${kind === 'guest' ? 'guests' : kind}`,
            handle: { title: KIND_COPY[kind].title },
            element: (
              <RequireAuth requiredPerm="console_user_view">
                <UsersListPage kind={kind} />
              </RequireAuth>
            ),
          })),
          {
            // Optional :sessionId deep-links the session details drawer.
            path: 'users/sessions/:sessionId?',
            handle: { title: 'Sessions', recordLabel: 'Session', idParam: 'sessionId' },
            element: (
              <RequireAuth requiredPerm="console_session_view">
                <SessionsPage />
              </RequireAuth>
            ),
          },
          {
            // Static segments above (system/staff/guests/sessions) outrank the
            // param route; profile URLs stay /users/<ID> for every user kind.
            path: 'users/:id/:tab?',
            handle: { title: 'User', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_user_view">
                <UserDetailPage />
              </RequireAuth>
            ),
          },
          {
            path: 'tour-operators',
            handle: { title: 'Tour operators' },
            element: (
              <RequireAuth requiredPerm="console_top_view">
                <TourOperatorsListPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'tour-operators/configs' outranks the :id splat below.
            path: 'tour-operators/configs',
            handle: { title: 'Batch configuration' },
            element: (
              <RequireAuth requiredPerm="console_top_view">
                <BatchConfigPage />
              </RequireAuth>
            ),
          },
          {
            // The routed create flow (the one non-modal create form — the
            // palette + list page navigate here instead of ?new=1): easy
            // onboarding, which falls back to the advanced create wizard when
            // the environment cannot run it.
            path: 'tour-operators/new',
            handle: { title: 'New tour operator' },
            element: (
              <RequireAuth requiredPerm="console_top_manage">
                <Suspense fallback={<RouteFallback />}>
                  <EasyOnboardingPage />
                </Suspense>
              </RequireAuth>
            ),
          },
          {
            // The create wizard's partner path (metaplan G1): the integration partner, its
            // technical contact and its sandbox in one POST; lands on the sandbox's setup.
            path: 'tour-operators/new/partner',
            handle: { title: 'New integration partner' },
            element: (
              <RequireAuth requiredPerm="console_partners_manage">
                <PartnerCreatePage />
              </RequireAuth>
            ),
          },
          {
            // The advanced create wizard; static segments outrank the :id splat.
            path: 'tour-operators/new/advanced',
            handle: { title: 'New tour operator' },
            element: (
              <RequireAuth requiredPerm="console_top_manage">
                <TourOperatorSetupPage mode="create" />
              </RequireAuth>
            ),
          },
          {
            // Easy onboarding after creation (Choose tours → Ready + the tray);
            // the static 'onboard' segment outranks the :id/* splat below.
            path: 'tour-operators/:id/onboard',
            handle: { title: 'Easy setup', detail: true },
            element: (
              <RequireAuth requiredPerm="console_top_view">
                <Suspense fallback={<RouteFallback />}>
                  <EasyOnboardingPage />
                </Suspense>
              </RequireAuth>
            ),
          },
          {
            // Static 'setup' segment outranks the :id/* splat below (same
            // ranking precedent as 'configs' above).
            path: 'tour-operators/:id/setup/:step?',
            handle: { title: 'Tour operator setup', detail: true, sectionParam: 'step' },
            element: (
              <RequireAuth requiredPerm="console_top_view">
                <TourOperatorSetupPage mode="setup" />
              </RequireAuth>
            ),
          },
          {
            // Splat: tab / subtab / record-details segments are URL-driven,
            // e.g. /tour-operators/9/configuration/history/123
            path: 'tour-operators/:id/*',
            handle: { title: 'Tour operator', detail: true, sectionParam: '*' },
            element: (
              <RequireAuth requiredPerm="console_top_view">
                <TourOperatorEditPage />
              </RequireAuth>
            ),
          },
          {
            path: 'bookings',
            handle: { title: 'Bookings' },
            element: (
              <RequireAuth requiredPerm="console_bookings_view">
                <BookingsPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'bookings' above outranks the param route.
            path: 'bookings/:id/:tab?',
            handle: { title: 'Booking', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_bookings_view">
                <BookingDetailPage />
              </RequireAuth>
            ),
          },
          {
            path: 'orders',
            handle: { title: 'Orders' },
            element: (
              <RequireAuth requiredPerm="console_orders_view">
                <OrdersPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'orders' above outranks the param route.
            path: 'orders/:id/:tab?',
            handle: { title: 'Order', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_orders_view">
                <OrderDetailPage />
              </RequireAuth>
            ),
          },
          {
            path: 'delivery-logs',
            handle: { title: 'Delivery logs' },
            element: (
              <RequireAuth requiredPerm="console_event_delivery_view">
                <DeliveryLogsPage />
              </RequireAuth>
            ),
          },
          {
            // One route serves the grid and the detail-dialog overlay: the
            // dialog is route-driven (/import-runs/:id/:tab?) so each tab is
            // deep-linkable and browser-back closes it.
            path: 'import-runs/:id?/:tab?',
            // No `sectionParam`: the dialog's tab labels live inside
            // ImportRunDetailDialog, not on this page, and the run id in the
            // title already tells two history entries apart.
            handle: { title: 'Imports', recordLabel: 'Import run' },
            element: (
              <RequireAuth requiredPerm="console_import_runs_view">
                <ImportRunsPage />
              </RequireAuth>
            ),
          },
          {
            // Same route-driven dialog pattern as import-runs.
            path: 'api-requests/:id?/:tab?',
            // As above — the dialog owns its tab labels.
            handle: { title: 'API requests', recordLabel: 'API request' },
            element: (
              <RequireAuth requiredPerm="console_api_requests_view">
                <ApiRequestsPage />
              </RequireAuth>
            ),
          },
          {
            // Optional :id deep-links the subscription detail drawer.
            path: 'subscriptions/:id?',
            handle: { title: 'Webhook subscriptions', recordLabel: 'Subscription' },
            element: (
              <RequireAuth requiredPerm="console_restech_view">
                <SubscriptionsPage />
              </RequireAuth>
            ),
          },
          {
            // The integration partner registry (metaplan G1, GATE D2).
            path: 'partners',
            handle: { title: 'Partners' },
            element: (
              <RequireAuth requiredPerm="console_partners_view">
                <PartnersPage />
              </RequireAuth>
            ),
          },
          {
            path: 'partners/:id',
            handle: { title: 'Partner', detail: true },
            element: (
              <RequireAuth requiredPerm="console_partners_view">
                <PartnerDetailPage />
              </RequireAuth>
            ),
          },
          {
            // External_Access_Log (metaplan G2): every external call, attributed.
            path: 'external-access',
            handle: { title: 'External access' },
            element: (
              <RequireAuth requiredPerm="console_external_access_view">
                <ExternalAccessPage />
              </RequireAuth>
            ),
          },
          {
            // Dashboard self-signup invite links; optional :id deep-links the detail drawer.
            path: 'onboarding-invites/:id?',
            handle: { title: 'Onboarding invites', recordLabel: 'Invite' },
            element: (
              <RequireAuth requiredPerm="console_onboarding_invites_view">
                <OnboardingInvitesPage />
              </RequireAuth>
            ),
          },
          {
            // Requests from tenants (v1: a self-signup client's go-live request). The page also
            // checks console_preflight_manage: both are today's Go live permissions.
            path: 'tenant-requests',
            handle: { title: 'Tenant requests' },
            element: (
              <RequireAuth requiredPerm="console_top_manage">
                <Suspense fallback={<RouteFallback />}>
                  <TenantRequestsPage />
                </Suspense>
              </RequireAuth>
            ),
          },
          {
            // Optional :id deep-links the credential detail drawer.
            path: 'api-credentials/:id?',
            handle: { title: 'API credentials', recordLabel: 'Credential' },
            element: (
              <RequireAuth requiredPerm="console_api_credentials_view">
                <ApiCredentialsPage />
              </RequireAuth>
            ),
          },
          {
            // Optional :id deep-links the mapping detail drawer. The id is a
            // COMPOSITE string ('aeid:1234'), so it must stay URL-encoded.
            path: 'ext-ids/:id?',
            handle: { title: 'External ID mappings', recordLabel: 'Mapping' },
            element: (
              <RequireAuth requiredPerm="console_ext_ids_view">
                <ExtIdsPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'delivery-logs' above outranks the param route.
            path: 'delivery-logs/:id/:tab?',
            handle: { title: 'Delivery log', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_event_delivery_view">
                <DeliveryLogDetailPage />
              </RequireAuth>
            ),
          },
          {
            path: 'tours',
            handle: { title: 'Tours' },
            element: (
              <RequireAuth requiredPerm="console_tours_view">
                <ToursListPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'tours' above outranks the param route.
            path: 'tours/:id/:tab?',
            handle: { title: 'Tour', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_tours_view">
                <TourDetailPage />
              </RequireAuth>
            ),
          },
          {
            path: 'regions',
            handle: { title: 'Regions' },
            element: (
              <RequireAuth requiredPerm="console_regions_view">
                <RegionsListPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'regions' above outranks the param route.
            path: 'regions/:id/:tab?',
            handle: { title: 'Region', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_regions_view">
                <RegionDetailPage />
              </RequireAuth>
            ),
          },
          {
            path: 'launch-policies',
            handle: { title: 'Launch policies' },
            element: (
              <RequireAuth requiredPerm="console_launch_policy_view">
                <LaunchPoliciesListPage />
              </RequireAuth>
            ),
          },
          {
            path: 'notifications',
            handle: { title: 'Notifications' },
            element: (
              <RequireAuth requiredPerm="console_notifications_view">
                <NotificationsListPage />
              </RequireAuth>
            ),
          },
          {
            // Static siblings (blocks/themes/…) outrank this param route in
            // react-router v6 ranking; the page guards non-numeric ids itself.
            path: 'notifications/:id',
            handle: { title: 'Notification', detail: true },
            element: (
              <RequireAuth requiredPerm="console_notifications_view">
                <NotificationEditPage />
              </RequireAuth>
            ),
          },
          {
            // Static segment outranks the notifications/:id param sibling.
            path: 'notifications/anatomy',
            handle: { title: 'Anatomy' },
            element: (
              <RequireAuth requiredPerm="console_notifications_view">
                <NotificationAnatomyPage />
              </RequireAuth>
            ),
          },
          {
            // Static segment outranks the notifications/:id param sibling.
            path: 'notifications/overview',
            handle: { title: 'Notification overview' },
            element: (
              <RequireAuth requiredPerm="console_notifications_view">
                <NotificationTimelinePage />
              </RequireAuth>
            ),
          },
          {
            // Static segment outranks the notifications/:id param sibling.
            path: 'notifications/rulesets',
            handle: { title: 'Rulesets' },
            element: (
              <RequireAuth requiredPerm="console_notification_rulesets_view">
                <RulesetsListPage />
              </RequireAuth>
            ),
          },
          {
            path: 'notifications/rulesets/:id',
            handle: { title: 'Ruleset', detail: true },
            element: (
              <RequireAuth requiredPerm="console_notification_rulesets_view">
                <RulesetEditPage />
              </RequireAuth>
            ),
          },
          {
            path: 'notifications/find-replace',
            handle: { title: 'Find & replace' },
            element: (
              <RequireAuth requiredPerm="console_content_search">
                <FindReplacePage />
              </RequireAuth>
            ),
          },
          {
            path: 'notifications/blocks',
            handle: { title: 'Blocks' },
            element: (
              <RequireAuth requiredPerm="console_notification_blocks_view">
                <NotificationBlocksListPage />
              </RequireAuth>
            ),
          },
          {
            path: 'notifications/features',
            handle: { title: 'Notification feature flags' },
            element: (
              <RequireAuth requiredPerm="console_notification_features_view">
                <NotificationFeatureFlagsPage />
              </RequireAuth>
            ),
          },
          {
            path: 'notifications/reseller-policies',
            handle: { title: 'Reseller policies' },
            element: (
              <RequireAuth requiredPerm="console_notification_features_view">
                <ResellerPoliciesPage />
              </RequireAuth>
            ),
          },
          {
            path: 'notifications/block-lists',
            handle: { title: 'Block lists' },
            element: (
              <RequireAuth requiredPerm="console_block_lists_view">
                <BlockListsListPage />
              </RequireAuth>
            ),
          },
          {
            path: 'notifications/canned',
            handle: { title: 'Canned notifications' },
            element: (
              <RequireAuth requiredPerm="console_canned_notifications_view">
                <CannedNotificationsListPage />
              </RequireAuth>
            ),
          },
          {
            path: 'notifications/themes',
            handle: { title: 'Themes' },
            element: (
              <RequireAuth requiredPerm="console_notification_themes_view">
                <ThemesListPage />
              </RequireAuth>
            ),
          },
          {
            // Static segment outranks the notifications/:id param sibling.
            path: 'notifications/design-imports',
            handle: { title: 'Design imports' },
            element: (
              <RequireAuth requiredPerm="console_composition_view">
                <MaterialsPage />
              </RequireAuth>
            ),
          },
          {
            path: 'notifications/design-imports/proposals/:id',
            handle: { title: 'Design proposal', detail: true },
            element: (
              <RequireAuth requiredPerm="console_composition_view">
                <ProposalReviewPage />
              </RequireAuth>
            ),
          },
          {
            path: 'notifications/language-policies',
            handle: { title: 'Language policies' },
            element: (
              <RequireAuth requiredPerm="console_language_policies_view">
                <LanguagePoliciesListPage />
              </RequireAuth>
            ),
          },
          {
            path: 'notifications/language-aliases',
            handle: { title: 'Language aliases' },
            element: (
              <RequireAuth requiredPerm="console_language_aliases_view">
                <LanguageAliasesListPage />
              </RequireAuth>
            ),
          },
          {
            path: 'notifications/test-render',
            handle: { title: 'Test render' },
            element: (
              <RequireAuth requiredPerm="console_notification_blocks_view">
                <TestRenderPage />
              </RequireAuth>
            ),
          },
          {
            // The console agent (backend metaplan A9): AI notification editing in a chat, on one
            // tenant (?tenant=), proposals reviewed and applied by a person in the panel beside it.
            // Static segment outranks the notifications/:id param sibling.
            path: 'notifications/ai-agent',
            handle: { title: 'AI agent' },
            element: (
              <RequireAuth requiredPerm="console_notification_mcp_read">
                <AIAgentPage />
              </RequireAuth>
            ),
          },
          {
            path: 'otas',
            handle: { title: 'OTAs' },
            element: (
              <RequireAuth requiredPerm="console_ota_view">
                <OTAsListPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'otas' above outranks the param route.
            path: 'otas/:id',
            handle: { title: 'OTA', detail: true },
            element: (
              <RequireAuth requiredPerm="console_ota_view">
                <OTADetailPage />
              </RequireAuth>
            ),
          },
          {
            path: 'trip-types',
            handle: { title: 'Trip types' },
            element: (
              <RequireAuth requiredPerm="console_trip_types_view">
                <TripTypesListPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'trip-types' above outranks the param route.
            path: 'trip-types/:id/:tab?',
            handle: { title: 'Trip type', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_trip_types_view">
                <TripTypeDetailPage />
              </RequireAuth>
            ),
          },
          {
            path: 'pre-trip-items',
            handle: { title: 'Trip items' },
            element: (
              <RequireAuth requiredPerm="console_pretrip_view">
                <PreTripItemsPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'pre-trip-items' above outranks the param route.
            path: 'pre-trip-items/:id/:tab?',
            handle: { title: 'Trip item', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_pretrip_view">
                <PreTripItemDetailPage />
              </RequireAuth>
            ),
          },
          {
            path: 'pre-trip-item-categories/:id/:tab?',
            handle: { title: 'Trip item category', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_pretrip_view">
                <PreTripItemCategoryDetailPage />
              </RequireAuth>
            ),
          },
          {
            path: 'activities',
            handle: { title: 'Activities' },
            element: (
              <RequireAuth requiredPerm="console_activities_view">
                <ActivitiesListPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'activities' above outranks the param route.
            path: 'activities/:id/:tab?',
            handle: { title: 'Activity', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_activities_view">
                <ActivityRecordPage />
              </RequireAuth>
            ),
          },
          {
            path: 'activity-categories',
            handle: { title: 'Activity categories' },
            element: (
              <RequireAuth requiredPerm="console_activities_view">
                <ActivityCategoriesListPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'activity-categories' above outranks the param route.
            path: 'activity-categories/:id/:tab?',
            handle: { title: 'Activity category', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_activities_view">
                <CategoryRecordPage />
              </RequireAuth>
            ),
          },
          {
            path: 'vehicles',
            handle: { title: 'Vehicles' },
            element: (
              <RequireAuth requiredPerm="console_fleet_view">
                <VehiclesPage />
              </RequireAuth>
            ),
          },
          {
            path: 'bus-stops',
            handle: { title: 'Bus stops' },
            element: (
              <RequireAuth requiredPerm="console_fleet_view">
                <BusStopsListPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'bus-stops' above outranks the param route.
            path: 'bus-stops/:id/:tab?',
            handle: { title: 'Bus stop', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_fleet_view">
                <BusStopDetailPage />
              </RequireAuth>
            ),
          },
          {
            path: 'line-sets',
            handle: { title: 'Line sets' },
            element: (
              <RequireAuth requiredPerm="console_fleet_view">
                <LineSetsListPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'line-sets' above outranks the param route.
            path: 'line-sets/:id/:tab?',
            handle: { title: 'Line set', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_fleet_view">
                <LineSetDetailPage />
              </RequireAuth>
            ),
          },
          {
            path: 'itineraries',
            handle: { title: 'Itineraries' },
            element: (
              <RequireAuth requiredPerm="console_itineraries_view">
                <ItinerariesListPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'itineraries' above outranks the param route.
            path: 'itineraries/:id/:tab?',
            handle: { title: 'Itinerary', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_itineraries_view">
                <ItineraryRecordPage />
              </RequireAuth>
            ),
          },
          {
            path: 'preflight-templates',
            handle: { title: 'Preflight templates' },
            element: (
              <RequireAuth requiredPerm="console_preflight_view">
                <PreflightTemplatesListPage />
              </RequireAuth>
            ),
          },
          {
            path: 'preflight-groups',
            handle: { title: 'Checklist groups' },
            element: (
              <RequireAuth requiredPerm="console_preflight_view">
                <PreflightGroupsListPage />
              </RequireAuth>
            ),
          },
          {
            path: 'preflight-items',
            handle: { title: 'Preflight items' },
            element: (
              <RequireAuth requiredPerm="console_preflight_view">
                <PreflightItemsListPage />
              </RequireAuth>
            ),
          },
          {
            path: 'preflight-user-groups',
            handle: { title: 'Preflight user groups' },
            element: (
              <RequireAuth requiredPerm="console_preflight_view">
                <PreflightUserGroupsPage />
              </RequireAuth>
            ),
          },
          {
            path: 'tag-category-sets',
            handle: { title: 'Tag category sets' },
            element: (
              <RequireAuth requiredPerm="console_preflight_view">
                <TagCategorySetsListPage />
              </RequireAuth>
            ),
          },
          {
            path: 'tag-name-aliases',
            handle: { title: 'Tag name aliases' },
            element: (
              <RequireAuth requiredPerm="console_tag_aliases_view">
                <TagNameAliasesListPage />
              </RequireAuth>
            ),
          },
          {
            // Translation glossary (backend metaplan A6): per-operator keep-verbatim terms and
            // preferred translations applied by every machine translation.
            path: 'translation-glossary',
            handle: { title: 'Translation glossary' },
            element: (
              <RequireAuth requiredPerm="console_translation_glossary_view">
                <TranslationGlossaryPage />
              </RequireAuth>
            ),
          },
          {
            path: 'tag-canonicals',
            handle: { title: 'Canonicals' },
            element: (
              <RequireAuth requiredPerm="console_tag_aliases_view">
                <TagCanonicalsListPage />
              </RequireAuth>
            ),
          },
          {
            path: 'tag-schemas',
            handle: { title: 'Tag schemas' },
            element: (
              <RequireAuth requiredPerm="console_tag_schemas_view">
                <TagSchemasListPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'tag-schemas' above outranks the param route.
            path: 'tag-schemas/:id/:tab?',
            handle: { title: 'Tag schema', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_tag_schemas_view">
                <TagSchemaDetailPage />
              </RequireAuth>
            ),
          },
          {
            path: 'tag-categories',
            handle: { title: 'Tag categories' },
            element: (
              <RequireAuth requiredPerm="console_tag_categories_view">
                <TagCategoriesListPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'tag-categories' above outranks the param route.
            path: 'tag-categories/:id/:tab?',
            handle: { title: 'Tag category', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_tag_categories_view">
                <TagCategoryDetailPage />
              </RequireAuth>
            ),
          },
          {
            path: 'tags',
            handle: { title: 'Tags' },
            element: (
              <RequireAuth requiredPerm="console_tags_view">
                <TagsListPage />
              </RequireAuth>
            ),
          },
          {
            // Static 'tags' above outranks the param route.
            path: 'tags/:id/:tab?',
            handle: { title: 'Tag', detail: true, sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_tags_view">
                <TagDetailPage />
              </RequireAuth>
            ),
          },
          {
            path: 'rbac/template-groups',
            handle: { title: 'Access group template groups' },
            element: (
              <RequireAuth requiredPerm="console_rbac_view">
                <TemplateGroupsListPage />
              </RequireAuth>
            ),
          },
          {
            path: 'rbac/templates',
            handle: { title: 'Access group templates' },
            element: (
              <RequireAuth requiredPerm="console_rbac_view">
                <TemplatesListPage />
              </RequireAuth>
            ),
          },
          {
            // Optional `:id` deep-links a specific access group (tenant resolved
            // server-side); the list is shown with or without a selected tenant.
            path: 'rbac/access-groups/:id?',
            handle: { title: 'Access groups', recordLabel: 'Access group' },
            element: (
              <RequireAuth requiredPerm="console_rbac_view">
                <AccessGroupsListPage />
              </RequireAuth>
            ),
          },
          {
            // Optional :taskId deep-links the task details dialog.
            path: 'bgtasks/:taskId?',
            handle: { title: 'Background tasks', recordLabel: 'Task', idParam: 'taskId' },
            element: (
              <RequireAuth>
                <BgTasksPage />
              </RequireAuth>
            ),
          },
          {
            path: 'sync/products',
            handle: { title: 'Product sync' },
            element: (
              <RequireAuth requiredPerm="console_bg_sync_products">
                <ProductSyncPage />
              </RequireAuth>
            ),
          },
          {
            path: 'ai-import',
            handle: { title: 'AI import' },
            element: (
              <RequireAuth requiredPerm="console_bg_ai_import">
                <AiImportPage />
              </RequireAuth>
            ),
          },
          {
            path: 'ai-import/:taskId/review',
            handle: { title: 'AI import review', recordLabel: 'Task', idParam: 'taskId' },
            element: (
              <RequireAuth requiredPerm="console_bg_ai_import">
                <DraftsReviewPage />
              </RequireAuth>
            ),
          },
          {
            path: 'sync/bookings',
            handle: { title: 'Booking sync' },
            element: (
              <RequireAuth requiredPerm="console_bg_sync_bookings">
                <BookingSyncPage />
              </RequireAuth>
            ),
          },
          {
            path: 'sync/aux-info',
            handle: { title: 'Aux info sync' },
            element: (
              <RequireAuth requiredPerm="console_bg_aux_sync">
                <AuxSyncPage />
              </RequireAuth>
            ),
          },
          {
            path: 'sync/aux-info/:taskId/review',
            handle: { title: 'Aux info review', recordLabel: 'Task', idParam: 'taskId' },
            element: (
              <RequireAuth requiredPerm="console_bg_aux_sync">
                <AuxSyncReviewPage />
              </RequireAuth>
            ),
          },
          {
            path: 'sync/orders',
            handle: { title: 'Order sync' },
            element: (
              <RequireAuth requiredPerm="console_bg_sync_orders">
                <OrderSyncPage />
              </RequireAuth>
            ),
          },
          {
            // Splat: /audit/<eventId> opens the details dialog (deep-linkable).
            path: 'audit/*',
            handle: { title: 'Audit log', recordLabel: 'Event', idParam: '*' },
            element: (
              <RequireAuth requiredPerm="console_audit_view">
                <AuditLogPage />
              </RequireAuth>
            ),
          },
          {
            // AI spend ledger (backend metaplan A5): metered LLM calls and their cost.
            path: 'ai-spend',
            handle: { title: 'AI spend' },
            element: (
              <RequireAuth requiredPerm="console_ai_spend_view">
                <AISpendPage />
              </RequireAuth>
            ),
          },
          {
            // AI changes (backend metaplan A3c): change sets — MCP / console-agent / design
            // import applies, translation syncs, reverts — reviewed record by record and reverted.
            path: 'ai-changes',
            handle: { title: 'AI changes' },
            element: (
              <RequireAuth requiredPerm="console_ai_changes_view">
                <AIChangesPage />
              </RequireAuth>
            ),
          },
          {
            // A8 (MCP editing, M24 rev 3): production tenants' MCP applies / reverts waiting for a
            // person, and the review page of an MCP proposal (real-booking renders, Apply / Reject).
            path: 'ai-changes/pending',
            handle: { title: 'Pending MCP changes' },
            element: (
              <RequireAuth requiredPerm="console_ai_changes_view">
                <AIPendingChangesPage />
              </RequireAuth>
            ),
          },
          {
            // One pending change on its own page: a client's revert of a whole content batch has
            // no one change set to carry it (client MCP plan §4.7 "Batches", WPS FE-07).
            path: 'ai-changes/pending/:id',
            handle: { title: 'Pending AI change', detail: true },
            element: (
              <RequireAuth requiredPerm="console_ai_changes_view">
                <AIPendingChangePage />
              </RequireAuth>
            ),
          },
          {
            path: 'ai-changes/proposals/:id',
            handle: { title: 'AI proposal', detail: true },
            element: (
              <RequireAuth requiredPerm="console_ai_changes_view">
                <AIProposalReviewPage />
              </RequireAuth>
            ),
          },
          {
            path: 'ai-changes/:id',
            handle: { title: 'AI change', detail: true },
            element: (
              <RequireAuth requiredPerm="console_ai_changes_view">
                <AIChangeDetailPage />
              </RequireAuth>
            ),
          },
          {
            // Support tickets clients' AI escalated over MCP (client MCP plan §5.4.1, WPS FE-03).
            path: 'support-tickets',
            handle: { title: 'Support tickets' },
            element: (
              <RequireAuth requiredPerm="console_support_tickets_view">
                <SupportTicketsPage />
              </RequireAuth>
            ),
          },
          {
            // The backend's `console_path` (the e-mail sink links here): `TO-` + 8 characters.
            path: 'support-tickets/:publicId',
            handle: { title: 'Support ticket', detail: true, idParam: 'publicId' },
            element: (
              <RequireAuth requiredPerm="console_support_tickets_view">
                <SupportTicketDetailPage />
              </RequireAuth>
            ),
          },
          {
            // Ops' word on a problem on our side, quoted to clients' AI by the triage check
            // `platform_notice` (client MCP plan §5.2, WPS FE-03). `?edit=<id>` / `?new` open it.
            path: 'platform-notices',
            handle: { title: 'Platform notices' },
            element: (
              <RequireAuth requiredPerm="console_platform_notices_manage">
                <PlatformNoticesPage />
              </RequireAuth>
            ),
          },
          {
            // Client MCP limit hits by UTC day, tenant and limit (client MCP plan §7.6, WPS FE-03).
            path: 'mcp-limit-hits',
            handle: { title: 'MCP limit hits' },
            element: (
              <RequireAuth requiredPerm="console_mcp_limits_view">
                <McpLimitHitsPage />
              </RequireAuth>
            ),
          },
          {
            // Clients' MCP diagnose runs, the LLM judge's verdicts and the human review queue
            // (client MCP plan §5.5, §7.3 #4; WPS FE-07, backend P4-03).
            path: 'mcp-diagnose-runs',
            handle: { title: 'Diagnose reviews' },
            element: (
              <RequireAuth requiredPerm="console_entity_ai">
                <McpDiagnoseRunsPage />
              </RequireAuth>
            ),
          },
          {
            path: 'mcp-diagnose-runs/:taskId',
            handle: { title: 'Diagnose run', detail: true, idParam: 'taskId' },
            element: (
              <RequireAuth requiredPerm="console_entity_ai">
                <McpDiagnoseRunPage />
              </RequireAuth>
            ),
          },
          {
            // What clients' AI could not resolve, from daily counters (client MCP plan §12.6 #6,
            // WPS FE-05).
            path: 'knowledge-gaps',
            handle: { title: 'Knowledge gaps' },
            element: (
              <RequireAuth requiredPerm="console_knowledge_gaps_view">
                <KnowledgeGapsPage />
              </RequireAuth>
            ),
          },
          {
            path: 'changes',
            handle: { title: 'Changes' },
            element: (
              <RequireAuth requiredPerm="console_audit_view">
                <ChangesListPage />
              </RequireAuth>
            ),
          },
          {
            path: 'presets',
            handle: { title: 'Grid presets' },
            element: (
              <RequireAuth requiredPerm="console_presets_manage">
                <PresetsManagePage />
              </RequireAuth>
            ),
          },
          {
            // Help articles: /help (landing) and /help/<scope>#<story-id>.
            // Auth-only — help carries no tenant data, so no permission gate.
            path: 'help/:scope?',
            handle: { title: 'Help', detail: true },
            element: (
              <RequireAuth>
                <Suspense fallback={<RouteFallback />}>
                  <HelpPage />
                </Suspense>
              </RequireAuth>
            ),
          },
          {
            // /blacklists and /blacklists/<tab> (rules | event-groups)
            path: 'blacklists/:tab?',
            handle: { title: 'Blacklists', sectionParam: 'tab' },
            element: (
              <RequireAuth requiredPerm="console_blacklist_view">
                <BlacklistsPage />
              </RequireAuth>
            ),
          },
        ],
      },

      // Fallback
      { path: '*', element: <Navigate to="/" replace />, handle: { redirect: true } },
    ],
  },
]);
